import * as Yup from 'yup';
import { isValidPhoneNumber } from '../../lib/phone';
import type { CountryCode } from '../../lib/phone';

/**
 * Onboarding asks for five things.
 *
 * The previous schema required eleven fields plus a logo, a legal entity name
 * and a submitted signature — none of which the API required. The marketing
 * profile and the legal details now live on the settings page and are demanded
 * by the pre-launch checklist instead.
 *
 * Required: business name, logo, industry type, business type, country.
 * Optional: business email (defaults server-side to the verified account
 * address), phone, website, address, city, postal code.
 */
export const validationSchema = Yup.object({
  language: Yup.string().required('Language is required'),
  business: Yup.object({
    name: Yup.string()
      .trim()
      .max(255, 'Business name must be at most 255 characters')
      .required('Business name is required'),
    logo: Yup.mixed<File>().required('Business logo is required'),
    industryType: Yup.string().required('Industry is required'),
    type: Yup.string().required('Business type is required'),
    country: Yup.string()
      .length(2, 'Select a country')
      .required('Country is required'),

    email: Yup.string()
      .trim()
      .email('Enter a valid email address')
      .max(255, 'Email must be at most 255 characters'),
    phoneNumber: Yup.string()
      .trim()
      .max(20, 'Phone number must be at most 20 characters')
      .test(
        'valid-phone',
        'Enter a valid phone number for the selected country',
        function validate(value) {
          const country = this.parent?.country as CountryCode | undefined;
          return isValidPhoneNumber(value, country);
        }
      ),
    website: Yup.string()
      .trim()
      .url('Enter a valid URL, including https://')
      .max(255, 'Website must be at most 255 characters'),
    address: Yup.string()
      .trim()
      .max(500, 'Address must be at most 500 characters'),
    city: Yup.string().trim().max(100, 'City must be at most 100 characters'),
    zipCode: Yup.string()
      .trim()
      .max(20, 'Postal code must be at most 20 characters'),
  }),
});
